import { useMemo } from "react";

// Extract the first numeric value out of a free-form string like
// "1 signed order", "$1.2M", "48%", "52 units" → 1, 1.2, 48, 52.
export function extractNumber(s) {
  if (s == null) return null;
  const str = String(s).replace(/[,_]/g, "");
  // Handle explicit K/M/B suffix.
  const m = str.match(/(-?\d+(?:\.\d+)?)([kKmMbB])?/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (isNaN(n)) return null;
  const suffix = (m[2] || "").toLowerCase();
  if (suffix === "k") return n * 1_000;
  if (suffix === "m") return n * 1_000_000;
  if (suffix === "b") return n * 1_000_000_000;
  return n;
}

/**
 * Sparkline — small inline SVG trend line.
 * points: [{month:'YYYY-MM', actual:string}]  target: string
 * Renders a polyline of numeric actuals over months, plus a dashed target line
 * (if extractable) and a green/amber dot at the end based on whether the last
 * actual has hit target.
 */
export default function Sparkline({ points, target, width = 140, height = 36, testId }) {
  const data = useMemo(() => {
    const numeric = (points || [])
      .map((p) => ({ month: p.month, value: extractNumber(p.actual) }))
      .filter((p) => p.value != null)
      .sort((a, b) => (a.month < b.month ? -1 : 1));
    return numeric;
  }, [points]);
  const targetNum = useMemo(() => extractNumber(target), [target]);

  if (data.length === 0) {
    return (
      <div className="mono-caps text-slate-300 text-[0.6rem]" data-testid={testId}>
        No numeric actuals yet
      </div>
    );
  }

  const values = data.map((d) => d.value);
  if (targetNum != null) values.push(targetNum);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pad = 4;
  const usableW = width - pad * 2;
  const usableH = height - pad * 2;

  const xFor = (i) => pad + (data.length === 1 ? usableW / 2 : (i * usableW) / (data.length - 1));
  const yFor = (v) => pad + usableH - ((v - min) / range) * usableH;

  const path = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xFor(i).toFixed(1)} ${yFor(d.value).toFixed(1)}`).join(" ");
  const lastX = xFor(data.length - 1);
  const lastY = yFor(data[data.length - 1].value);
  const hit = targetNum != null && data[data.length - 1].value >= targetNum;
  const dotColor = hit ? "#059669" : "#d97706";
  const targetY = targetNum != null ? yFor(targetNum) : null;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} data-testid={testId}>
      {targetY != null && (
        <line
          x1={pad}
          x2={width - pad}
          y1={targetY}
          y2={targetY}
          stroke="#94a3b8"
          strokeDasharray="3 3"
          strokeWidth="1"
        />
      )}
      <path d={path} fill="none" stroke="#1e3a8a" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastX} cy={lastY} r={3.2} fill={dotColor} />
    </svg>
  );
}
